import { captureRejectedParentOrigin } from '@core/sentry/utils';

/**
 * Height reporting for the booking widget when it runs inside a cross-origin
 * iframe on a customer's website.
 *
 * The parent page cannot read our document height across origins, so the app
 * measures itself and posts updates outward. The parent listens and sizes the
 * frame. See docs/architecture/iframe-embedding.md for the contract the parent
 * has to implement.
 *
 * Nothing here runs outside an iframe.
 */

/** Identifies our messages to the parent, so it can ignore other frames. */
export const APP_MESSAGE_SOURCE = 'booking-app';
/** Identifies the parent's messages to us. */
export const PARENT_MESSAGE_SOURCE = 'booking-parent';
/** Bumped only on a breaking change to the message shape. */
export const RESIZE_PROTOCOL_VERSION = 1;

/**
 * The URL parameter names, and the message `type` values. These are wire
 * contract consumed by customer websites we cannot see or update — named here
 * so a rename cannot land in one file and not another.
 */
export const EMBED_PARAM_FRAME_ID = 'frameId';
export const EMBED_PARAM_PARENT_ORIGIN = 'parentOrigin';
export const RESIZE_MESSAGE_TYPE = 'resize';
export const REQUEST_RESIZE_MESSAGE_TYPE = 'request-resize';

/**
 * Sub-pixel jitter from font metrics, zoom and fractional layout would other-
 * wise stream messages at the parent for a page that has not visibly moved.
 * Measured against the last height we SENT, not the last one we sampled, so a
 * slow 1px-at-a-time drift still reports once it accumulates past the bar.
 */
const MIN_HEIGHT_DELTA_PX = 2;

/**
 * Upper bound on what we will ask a customer's page for. The overlay cap that
 * bounds a dialog's contribution is a CSS rule, and restaurant theme colours
 * are string-interpolated into a <style> element (`rawColor` in
 * core/tenant/useRestaurantTheme.ts), so CSS is not a trustworthy sole bound on
 * a value we hand to someone else's layout. Far above any real page.
 */
const MAX_REPORTED_HEIGHT_PX = 20000;

/** The message we post outward on every height change. */
export interface ResizeMessage {
  source: typeof APP_MESSAGE_SOURCE;
  type: typeof RESIZE_MESSAGE_TYPE;
  version: typeof RESIZE_PROTOCOL_VERSION;
  /** Echoes the `frameId` URL parameter; null when the parent did not set one. */
  frameId: string | null;
  /** Full rendered content height in CSS pixels, rounded up. */
  height: number;
}

export interface IframeAutoResizeOptions {
  /** Host window. Injectable so the module is testable without globals. */
  win?: Window;
  /** Element to measure. Defaults to the app root, then `<body>`. */
  target?: HTMLElement | null;
}

type HostWindow = Window & {
  ResizeObserver?: typeof ResizeObserver;
  MutationObserver?: typeof MutationObserver;
};

/**
 * Marks the document as embedded so CSS can lift the `100vh` floor that
 * `min-h-screen` puts on the layout shells. Inside a frame that floor is the
 * frame's OWN current height, so once the parent applies a tall height the
 * content can never measure shorter and the frame is stuck tall. See
 * `html[data-embedded]` in src/index.css.
 */
const EMBEDDED_ATTR = 'data-embedded';

/**
 * An origin and nothing else. Anything carrying a path, query, fragment or
 * credentials was never an origin and is refused; everything else is
 * NORMALIZED rather than compared verbatim.
 *
 * The earlier `url.origin === raw` test was strict enough to reject the forms
 * a correct-intent integrator is most likely to paste — a trailing slash from
 * the address bar, `:443`, an uppercase host — and each rejection silently
 * downgraded that customer to the `"*"` default the docs tell them to avoid.
 * Failing open on the most common typo is worse than accepting a tidied value:
 * `targetOrigin` is a delivery FILTER, never a destination (the destination is
 * always `win.parent`, fixed by the frame tree), so a normalized origin can
 * only ever narrow who may receive the message.
 */
function normalizeParentOrigin(raw: string | null, win?: HostWindow): string | null {
  if (!raw) return null;

  const reject = () => {
    if (win) reportRejectedOrigin(win, raw);
    return null;
  };

  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return reject();
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') return reject();
  if (url.username || url.password) return reject();
  if (url.pathname !== '/' || url.search || url.hash) return reject();
  if (url.origin === 'null') return reject();

  return url.origin;
}

/**
 * Tell someone. A rejected origin is invisible on both sides otherwise: the
 * widget keeps working, and the customer believes they pinned an origin they
 * did not. Once per page load, and only when a value was actually supplied.
 */
const reportedRejection = new WeakSet<Window>();

function reportRejectedOrigin(win: HostWindow, raw: string) {
  // getEmbedParams re-parses on every call — twice per load, and twice more
  // per auth redirect — so without this the "once" below is not true.
  if (reportedRejection.has(win)) return;
  reportedRejection.add(win);

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(
      `[embed] parentOrigin ${JSON.stringify(raw)} is not a bare http(s) origin; ` +
        'falling back to "*". Expected e.g. "https://www.customer.com".',
    );
  }
  try {
    captureRejectedParentOrigin(raw, win.location.host);
  } catch {
    // Never let telemetry break height reporting.
  }
}

function resolveTarget(win: HostWindow, explicit?: HTMLElement | null): HTMLElement | null {
  if (explicit) return explicit;
  const doc = win.document;
  return doc.getElementById('root') ?? doc.body ?? doc.documentElement ?? null;
}

/**
 * Overlays render into a portal on `<body>`, outside the measured root, so a
 * selector is what finds them. `data-embed-overlay` is set by our own Dialog,
 * Sheet and AlertDialog content, and by ThreeDSChallengeOverlay, which uses
 * the Radix primitive directly.
 *
 * An explicit marker rather than a role selector, deliberately. `role="dialog"`
 * is also what Radix Popover renders — so a role-based boundary silently pulled
 * in the language picker — and matching only `dialog` previously missed
 * `alertdialog` entirely. The marker says what we mean.
 *
 * Measuring an overlay tells the parent one thing beyond page height: that a
 * modal is open. Accepted deliberately — the alternative is clipping a modal
 * the guest cannot dismiss. Our 3-D Secure overlay is a fixed-size spinner
 * card, so its height does not vary with the challenge, and no measured overlay
 * carries guest-specific content. An overlay that ever does must not be marked.
 *
 * NOT covered: the 3-D Secure challenge itself. `stripe.handleNextAction()`
 * injects Stripe's own fixed-position iframe on `<body>` with no marker of ours
 * and outside our stylesheet, so neither this measurement nor the embedded CSS
 * applies to it. Only our own "verifying with your bank" overlay is.
 */
const OVERLAY_SELECTOR = '[data-embed-overlay]';

/**
 * Full rendered height. `scrollHeight` catches content that overflows the box
 * while the bounding rect catches transforms and fractional layout; the larger
 * of the two is what the frame actually has to be.
 *
 * Portaled overlays are added on top of that. They sit outside the root and are
 * `position: fixed`, so they contribute nothing to its box — but they ARE
 * positioned against the frame's viewport, so a frame sized to the root alone
 * clips them, and `scrolling="no"` leaves no way to reach the clipped part.
 *
 * Overlays are sized in px rather than `vh` when embedded (see
 * `html[data-embedded]` in src/index.css). That is what makes this measurement
 * meaningful: a `vh`-sized modal on a short step measures as a fraction of a
 * short frame, so growing the frame to fit it would just chase a moving target
 * down toward the content height.
 */
function measureHeight(win: HostWindow, el: HTMLElement): number {
  const rect = el.getBoundingClientRect();
  let height = Math.max(rect.height, el.scrollHeight);

  // `offsetHeight`, not the bounding rect: Radix animates content in with
  // `zoom-in-95`, and a rect includes that transform — so a measurement one
  // frame after the portal mounts reads ~95% of the settled height and the
  // frame ends up permanently a few percent short. offsetHeight is the
  // untransformed layout height, and unlike scrollHeight it still respects the
  // max-height cap rather than reporting unclipped content.
  const overlays = win.document.querySelectorAll(OVERLAY_SELECTOR);
  overlays.forEach((overlay) => {
    height = Math.max(height, (overlay as HTMLElement).offsetHeight);
  });

  return Math.min(MAX_REPORTED_HEIGHT_PX, Math.ceil(height));
}

/**
 * What this frame was embedded with, remembered for the life of the page.
 *
 * The query string exists only on the first load: `navigate('/select-diners')`
 * rewrites the URL long before the guest reaches login, so by the time the
 * auth redirect asks, there is nothing left to read. Keyed by window, so each
 * frame on a page keeps its own answer — which is also what tells two widgets
 * apart without the shared store having to.
 */
const liveConfigs = new WeakMap<Window, { frameId: string | null; parentOrigin: string | null }>();

/**
 * The embed parameters have to outlive a full-page auth round trip. Login and
 * signup leave for the auth origin and return through a URL rebuilt from the
 * pathname plus a fixed param whitelist (`buildReturnUrl` in
 * core/auth/authRedirect.ts), which is why both that URL and the auth URL now
 * carry them explicitly. This store is the belt to that braces.
 *
 * Its load-bearing case is NOT a hard refresh, which reloads the parent's
 * iframe src and gets the parameters back. It is re-entry on the AUTH origin,
 * which keeps its own copy of this store: the OAuth callback returns to
 * `auth.v2.meitre.com/?code=…` with no embed parameters, and that redirect URI
 * is fixed in provider config, so URL forwarding structurally cannot cover it.
 * Same for AuthApp's storage-failure and error paths, which do full loads to
 * bare origins. Delete this layer and OAuth-inside-a-frame regresses silently.
 *
 * One store is shared by every same-origin frame on the page, so it holds a
 * map rather than two fixed keys: with two widgets embedded, fixed keys would
 * let one frame read the OTHER frame's id and hand the parent a message
 * attributed to the wrong widget. A map makes that ambiguity visible, and an
 * ambiguous store restores nothing at all.
 */
const CONFIG_STORE_KEY = 'embed:configs';

type StoredConfigs = Record<string, string>;

// Deliberately not `safeSessionStorage` from @shared/utils: that helper
// hardcodes `window`, and this module takes an injectable host window so the
// embedded case is testable. Don't "clean this up".
function readStoredConfigs(win: HostWindow): StoredConfigs {
  try {
    const raw = win.sessionStorage?.getItem(CONFIG_STORE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    // Arrays pass a bare `typeof === 'object'` and would yield a numeric index
    // as a frameId — a WRONG id rather than none.
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        ([, v]) => typeof v === 'string',
      ),
    ) as StoredConfigs;
  } catch {
    // Unavailable or corrupt storage is the same as an empty store.
    return {};
  }
}

/**
 * Newest-last insertion order caps the store. A parent that mints a fresh
 * frameId per load would otherwise add an entry every time, forever. The store
 * cannot RECOVER from that — a rotating id and two concurrent widgets look
 * identical here, and refusing to guess is the safer reading — but it must not
 * grow without bound in the guest's storage either.
 */
const MAX_STORED_CONFIGS = 4;

function rememberConfig(win: HostWindow, frameId: string | null, parentOrigin: string | null) {
  try {
    const configs = readStoredConfigs(win);
    // Delete before setting, so a re-write moves the key to newest.
    delete configs[frameId ?? ''];
    configs[frameId ?? ''] = parentOrigin ?? '';

    const keys = Object.keys(configs);
    for (const stale of keys.slice(0, Math.max(0, keys.length - MAX_STORED_CONFIGS))) {
      delete configs[stale];
    }
    win.sessionStorage?.setItem(CONFIG_STORE_KEY, JSON.stringify(configs));
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). Reporting
    // still works for this page load; only a later full load loses it.
  }
}

/**
 * The stored configuration, but only when there is exactly one — with several
 * widgets on the page there is no way to tell which entry belongs to this
 * frame, and guessing wrong misroutes the parent's resize.
 */
function restoreConfig(win: HostWindow): { frameId: string | null; parentOrigin: string | null } {
  const entries = Object.entries(readStoredConfigs(win));
  if (entries.length !== 1) return { frameId: null, parentOrigin: null };

  const [frameId, parentOrigin] = entries[0];
  return { frameId: frameId || null, parentOrigin: parentOrigin || null };
}

/**
 * The embed configuration for this frame, from the query string or, after a
 * navigation dropped it, from session storage.
 *
 * Exported so a full-page navigation OUT of this origin can carry the values
 * with it: session storage is per-origin, so the auth app cannot read what the
 * restaurant origin stored. See `redirectToAuth` in core/auth/authRedirect.ts.
 *
 * Returns nulls when the app is not embedded — there is nothing to carry.
 */
export function getEmbedParams(win: Window = window): {
  frameId: string | null;
  parentOrigin: string | null;
} {
  const host = win as HostWindow;
  if (host.self === host.top) return { frameId: null, parentOrigin: null };

  const params = new URLSearchParams(host.location.search);

  // Presence, not truthiness: `?frameId=` is the parent SAYING there is no
  // frame id, which is not the same as never mentioning it. Only a URL that
  // mentions neither falls back to the store.
  if (params.has(EMBED_PARAM_FRAME_ID) || params.has(EMBED_PARAM_PARENT_ORIGIN)) {
    const frameId = params.get(EMBED_PARAM_FRAME_ID) || null;
    const parentOrigin = normalizeParentOrigin(params.get(EMBED_PARAM_PARENT_ORIGIN), host);
    liveConfigs.set(host, { frameId, parentOrigin });
    rememberConfig(host, frameId, parentOrigin);
    return { frameId, parentOrigin };
  }

  // What this frame already learned beats anything the shared store holds.
  const live = liveConfigs.get(host);
  if (live) return live;

  const restored = restoreConfig(host);
  // A restored value is only as trustworthy as one arriving fresh, so it
  // clears the same gate. Not reported: a stale store is not a live typo.
  const config = {
    frameId: restored.frameId,
    parentOrigin: normalizeParentOrigin(restored.parentOrigin),
  };

  // Cache what was resolved, so a later write by another frame on this page
  // cannot take it back. Without this the restore branch re-derived from the
  // shared store on every call — and the call that matters is the one at
  // auth-redirect time, long after a second widget may have mounted.
  if (config.frameId || config.parentOrigin) liveConfigs.set(host, config);

  return config;
}

/**
 * Copies this frame's embed configuration onto an outgoing URL, so it survives
 * a full-page navigation. Accepts either shape the callers already hold — a
 * `URLSearchParams`, or the plain record `getAuthUrl` takes.
 *
 * No-op when not embedded, which is what keeps standalone auth URLs
 * byte-identical to what they were before this feature.
 */
export function applyEmbedParams(
  target: URLSearchParams | Record<string, string>,
  win: Window = window,
): void {
  const { frameId, parentOrigin } = getEmbedParams(win);
  const set = (key: string, value: string) => {
    if (target instanceof URLSearchParams) target.set(key, value);
    else target[key] = value;
  };

  if (frameId) set(EMBED_PARAM_FRAME_ID, frameId);
  if (parentOrigin) set(EMBED_PARAM_PARENT_ORIGIN, parentOrigin);
}

/**
 * Starts reporting height to the parent frame. Returns a teardown function
 * that disconnects the observer, drops the listeners and cancels any frame
 * still in flight. Safe to call when not embedded — it does nothing and hands
 * back a no-op.
 */
export function startIframeAutoResize(options: IframeAutoResizeOptions = {}): () => void {
  const win = (options.win ?? window) as HostWindow;

  // Outside an iframe there is no parent to talk to.
  if (win.self === win.top) return () => {};

  const target = resolveTarget(win, options.target);
  if (!target) return () => {};

  // Re-validated on the way out of storage: a restored value is only as
  // trustworthy as one arriving fresh, and must clear the same gate.
  const { frameId, parentOrigin } = getEmbedParams(win);
  // Absent or invalid falls back to '*', which is what the pre-existing
  // parent integrations expect.
  const targetOrigin = parentOrigin ?? '*';

  let stopped = false;
  let frameHandle: number | null = null;
  let forceNextSend = false;
  let lastSentHeight: number | null = null;

  const send = () => {
    const height = measureHeight(win, target);

    // Zero is never a legitimate height for this app, and it is reachable:
    // a display:none frame, or an empty loading shell now that the
    // min-h-screen floor is lifted. Posting it tells a conforming parent to
    // collapse the frame to nothing, which only a later observation can undo.
    if (height <= 0) return;

    const unchanged =
      lastSentHeight !== null && Math.abs(height - lastSentHeight) < MIN_HEIGHT_DELTA_PX;

    if (unchanged && !forceNextSend) return;

    forceNextSend = false;
    lastSentHeight = height;

    const message: ResizeMessage = {
      source: APP_MESSAGE_SOURCE,
      type: RESIZE_MESSAGE_TYPE,
      version: RESIZE_PROTOCOL_VERSION,
      frameId,
      height,
    };

    try {
      win.parent.postMessage(message, targetOrigin);
    } catch {
      // postMessage throws SecurityError when targetOrigin does not match the
      // parent's real origin — a stale stored origin, or a customer who moved
      // to a www host. Unguarded this throws inside the animation frame on
      // every measurement, in someone else's page. Swallow and keep going: the
      // next real change tries again, and the parent is already not listening.
    }
  };

  /**
   * Coalesces every trigger in the same frame into one measurement. This is
   * also the loop guard: the parent resizing the frame in response feeds back
   * as another observer callback, which either reports an unchanged height and
   * stops, or settles within a frame. No polling timer anywhere.
   */
  const schedule = (force = false) => {
    if (stopped) return;
    if (force) forceNextSend = true;
    if (frameHandle !== null) return;

    frameHandle = win.requestAnimationFrame(() => {
      frameHandle = null;
      if (stopped) return;
      send();
    });
  };

  const onParentMessage = (event: MessageEvent) => {
    if (stopped) return;
    // Only the frame that embeds us may ask.
    if (event.source !== win.parent) return;
    // With no validated parentOrigin there is nothing to compare against, so
    // the parent-identity check above is the gate.
    if (parentOrigin && event.origin !== parentOrigin) return;

    const data = event.data as Partial<{ source: string; type: string; frameId: string }> | null;
    if (!data || typeof data !== 'object') return;
    if (data.source !== PARENT_MESSAGE_SOURCE || data.type !== REQUEST_RESIZE_MESSAGE_TYPE) return;
    // Only enforceable when we were given an id to match against.
    if (frameId !== null && data.frameId !== frameId) return;

    // The parent only asks when it has lost track, so answer even if the
    // height has not moved.
    schedule(true);
  };

  const onLoad = () => schedule();

  // Late-loading images and stylesheets land on `load`; webfonts reflow text
  // after that again. Both are measured, not assumed.
  win.addEventListener('message', onParentMessage as EventListener);
  win.addEventListener('load', onLoad);

  win.document.documentElement?.setAttribute(EMBEDDED_ATTR, 'true');

  const observer = win.ResizeObserver ? new win.ResizeObserver(() => schedule()) : null;
  observer?.observe(target);

  // A portal mounting on <body> leaves the root's box untouched, so the resize
  // observer above never fires for it. This is what notices a dialog opening.
  // An overlay that grows AFTER it opens — an image loading in the experience
  // dialog, an error appearing in the terms dialog — changes no box this code
  // otherwise watches: the resize observer is on the root, and the overlay is
  // outside it. So each one is watched directly, and released when it closes:
  // the body callback fires for every toast and injected <style> too, so
  // re-observing unconditionally would retain detached nodes for the life of
  // the frame.
  const watchedOverlays = new Set<Element>();
  const syncOverlayWatchers = () => {
    const open = new Set<Element>(win.document.querySelectorAll(OVERLAY_SELECTOR));

    watchedOverlays.forEach((overlay) => {
      if (open.has(overlay)) return;
      observer?.unobserve(overlay);
      watchedOverlays.delete(overlay);
    });

    open.forEach((overlay) => {
      if (watchedOverlays.has(overlay)) return;
      observer?.observe(overlay);
      watchedOverlays.add(overlay);
    });
  };

  const portalObserver = win.MutationObserver
    ? new win.MutationObserver(() => {
        syncOverlayWatchers();
        schedule();
      })
    : null;
  if (portalObserver && win.document.body) {
    portalObserver.observe(win.document.body, { childList: true });
  }

  // `.catch` so a rejection cannot surface as an unhandled rejection in the
  // customer's page — Sentry's global handler would report it with no context.
  void win.document.fonts?.ready.then(() => schedule()).catch(() => {});

  // First measurement, as soon as the app has mounted.
  schedule();

  return () => {
    if (stopped) return;
    stopped = true;

    win.document.documentElement?.removeAttribute(EMBEDDED_ATTR);
    watchedOverlays.clear();
    observer?.disconnect();
    portalObserver?.disconnect();
    win.removeEventListener('message', onParentMessage as EventListener);
    win.removeEventListener('load', onLoad);

    if (frameHandle !== null) {
      win.cancelAnimationFrame(frameHandle);
      frameHandle = null;
    }
  };
}
